export function normalizeFive9ConversationsDatesToUtc<T extends { date?: unknown }>(conversations: T[]): T[] {
  if (!Array.isArray(conversations) || conversations.length === 0) {
    return [];
  }

  return conversations.map((conversation) => ({
    ...conversation,
    date: normalizeEasternDateStringToUtc(conversation?.date),
  }));
} 

export function normalizeEasternDateStringToUtc(value: unknown): unknown {
  if (value === null || value === undefined || value === '') {
    return value;
  }

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? value : value.toISOString();
  }

  const raw = String(value).trim();
  if (!raw) {
    return value;
  }

  // Already timezone-aware (Z or explicit +/- offset): keep standard UTC serialization.
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(raw)) {
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? value : parsed.toISOString();
  }

  // Treat timezone-less timestamps from Five9 as US Eastern local time, then convert to UTC.
  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s])(\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,7}))?$/,
  );
  if (!match) {
    return value;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] ?? '0');
  const fraction = match[7] ?? '';
  const millisecond = Number((fraction + '000').slice(0, 3));

  const easternOffsetMinutes = getEasternUtcOffsetMinutesForLocalDate(year, month, day, hour);
  const utcMillis = Date.UTC(year, month - 1, day, hour, minute, second, millisecond) - (easternOffsetMinutes * 60000);
  return new Date(utcMillis).toISOString();
}

function getEasternUtcOffsetMinutesForLocalDate(year: number, month: number, day: number, hour: number): number {
  const isDst = isEasternDstLocalDate(year, month, day, hour);
  // EDT = UTC-4, EST = UTC-5
  return isDst ? -240 : -300;
}

function isEasternDstLocalDate(year: number, month: number, day: number, hour: number): boolean {
  if (month < 3 || month > 11) {
    return false;
  }
  if (month > 3 && month < 11) {
    return true;
  }

  const secondSundayInMarch = getNthWeekdayOfMonth(year, 3, 0, 2);
  const firstSundayInNovember = getNthWeekdayOfMonth(year, 11, 0, 1);

  if (month === 3) {
    if (day > secondSundayInMarch) return true;
    if (day < secondSundayInMarch) return false;
    return hour >= 2;
  }

  if (day < firstSundayInNovember) return true;
  if (day > firstSundayInNovember) return false;
  return hour < 2;
}

function getNthWeekdayOfMonth(year: number, month: number, weekday: number, nth: number): number {
  const firstDay = new Date(year, month - 1, 1).getDay();
  const offset = (weekday - firstDay + 7) % 7;
  return 1 + offset + ((nth - 1) * 7);
}
